import { useState, useCallback, useEffect, useRef } from "react";

/**
 * useReportFilters: A premium custom hook for standardizing report filtering logic.
 *
 * This hook manages:
 * 1. Immediate filter state (for responsive rendering of text input fields).
 * 2. Debounced filter stats (for efficient API calling, preventing excess server load).
 * 3. Standardized pagination state & handlers.
 *
 * @param {Object} initialFilters - The default filter configuration.
 * @param {Number|Object} debounceOptions - Delay in ms OR object { delay, debounceKeys }.
 */
export const useReportFilters = (
  initialFilters = {},
  debounceOptions = 500,
  initialPageNum = 1
) => {
  const delay =
    typeof debounceOptions === "number"
      ? debounceOptions
      : debounceOptions?.delay || 500;

  const [filters, setFilters] = useState(initialFilters);
  const [debouncedFilters, setDebouncedFilters] = useState(initialFilters);
  const [pageNum, setPageNum] = useState(initialPageNum);
  const [pageSize, setPageSize] = useState(50);

  const timerRef = useRef(null);

  // Standard handler for all filters (Selects, DatePickers, Inputs)
  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === null || value === undefined ? undefined : value,
    }));

    // Automatically reset to page 1 on any filter change
    setPageNum(initialPageNum);
  }, [initialPageNum]);

  // Standard handler for table pagination
  const handlePageChange = useCallback((page, size) => {
    setPageNum(page);
    if (size) setPageSize(size);

    // Aesthetic optimization: scroll to top of viewport on page change
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Debounce logic for the API-triggering state
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [filters, delay]);

  return {
    // State
    filters, // Use for controlled UI components (Select, Input)
    debouncedFilters, // Use for API calls (dependencies in useEffect)
    pageNum,
    initialPageNum, // Certain APIs have 0-based pagination, certain APIs have 1-based pagination
    pageSize,

    // Setters / Handlers
    setFilters,
    setPageNum,
    setPageSize,
    handleFilterChange,
    handlePageChange,
  };
};

export default useReportFilters;
